<template>
  <b-modal
    ref="modal"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <template #modal-title>
      <span class="bloqueio-modal-title">
        <i class="fa fa-exclamation-triangle bloqueio-modal-title-icon" aria-hidden="true"></i>
        {{ titulo }}
      </span>
    </template>

    <div v-if="bloqueio" class="bloqueio-modal-content">
      <!-- Corpo da copy por família de guarda (TMS-8134). O motivo da SEFAZ/NSTech
           entra aqui **integral**: o backend deixou de truncá-lo exatamente porque a
           tela o cortava (SDK-101072, `Rejeição: IE d…`), e `white-space: pre-line`
           preserva as quebras sem nenhum `text-truncate`. -->
      <p class="bloqueio-modal-message">{{ copy.corpo }}</p>

      <!-- Janela do fornecedor. O limite vem sempre de `prazo.limite_horas`. -->
      <p
        v-if="copy.prazo"
        :class="[
          'bloqueio-modal-prazo',
          copy.prazoExpirado ? 'text-danger' : 'text-warning',
        ]"
      >
        <i class="fa fa-clock-o" aria-hidden="true"></i>
        {{ copy.prazo }}
      </p>

      <!-- A sequência que até hoje só existia como conhecimento tácito. -->
      <div v-if="passosVisiveis.length > 1" class="bloqueio-modal-passos">
        <p class="bloqueio-modal-passos-titulo">
          O cancelamento desta carga depende destes passos, nesta ordem:
        </p>
        <ol class="bloqueio-modal-passos-lista">
          <li
            v-for="passo in passosVisiveis"
            :key="`passo-${passo.codigo}`"
            :class="{
              'bloqueio-modal-passo-proximo': passo.codigo === codigoProximoPasso,
              'bloqueio-modal-passo-indisponivel': !passo.disponivel,
            }"
          >
            {{ passo.rotulo }}
            <span
              v-if="passo.codigo === codigoProximoPasso"
              class="badge badge-primary"
              >próximo</span
            >
            <small v-else-if="!passo.disponivel" class="text-muted">
              — {{ explicacaoIndisponibilidade(passo.motivoIndisponivel) }}
            </small>
          </li>
        </ol>
      </div>

      <!-- Ação primária fora de ordem: a tela diz qual passo vem antes em vez de
           oferecer o botão que queimaria o passo anterior (TMS-8133). -->
      <p v-if="copy.avisoSequencia" class="bloqueio-modal-sequencia">
        {{ copy.avisoSequencia }}
      </p>

      <!-- Composição declarada pelo backend, quando a ação primária tem etapas. -->
      <div
        v-if="acaoPrimaria && acaoPrimaria.etapas.length > 0"
        class="bloqueio-modal-etapas"
      >
        <p class="mb-1">Esta ação executa, em sequência:</p>
        <ol class="mb-0">
          <li v-for="(etapa, indice) in acaoPrimaria.etapas" :key="`etapa-${indice}`">
            {{ etapa }}
          </li>
        </ol>
      </div>

      <!-- Sem ação primária, o operador precisa saber por quê — e qual ação lhe
           falta — antes de pedir ajuda. Botão que devolve 403 é o antipadrão que
           esta tela elimina. -->
      <p v-if="!acaoPrimaria && copy.avisoSemAcaoPrimaria" class="bloqueio-modal-sem-acao">
        {{ copy.avisoSemAcaoPrimaria }}
      </p>

      <div class="bloqueio-modal-detalhes">
        <button
          type="button"
          class="btn btn-link btn-sm bloqueio-modal-detalhes-toggle"
          @click="detalhesAbertos = !detalhesAbertos"
        >
          <i
            :class="['fa', detalhesAbertos ? 'fa-caret-down' : 'fa-caret-right']"
            aria-hidden="true"
          ></i>
          Dados para o chamado
        </button>
        <table v-if="detalhesAbertos" class="table table-sm bloqueio-modal-detalhes-tabela">
          <tbody>
            <tr v-for="detalhe in detalhes" :key="`detalhe-${detalhe.rotulo}`">
              <th scope="row">{{ detalhe.rotulo }}</th>
              <td>{{ detalhe.valor }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bloqueio-modal-actions">
        <!-- `acaoPrimaria` já chega cruzada com a sequência: a copy só devolve
             ação aqui quando ela é o próximo passo disponível da lista acima
             (TMS-8133). Um `v-if` que olhasse apenas a existência da ação
             reintroduziria o defeito D1 no ponto exato do clique. -->
        <button
          v-if="acaoPrimaria"
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="executar(acaoPrimaria.codigo)"
        >
          {{ loading ? "Executando..." : acaoPrimaria.rotulo }}
        </button>
        <button
          v-if="acaoSecundaria"
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="executar(acaoSecundaria.codigo)"
        >
          {{ acaoSecundaria.rotulo }}
        </button>
        <button
          v-if="acaoAlternativa"
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="executar(acaoAlternativa.codigo)"
        >
          {{ acaoAlternativa.rotulo }}
        </button>
        <button type="button" class="btn btn-secondary" @click="fechar">
          Fechar
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
/**
 * Modal do bloqueio de cancelamento de expedição — TMS-8133.
 *
 * Decisão registrada no ADR-0001 deste repositório: modal dedicado dirigido pela
 * chave `bloqueio` em vez de `ReturnApi`/Simplert, porque o Simplert tem no
 * máximo dois botões e a copy exige três, e porque `onConfirm` é callback e não
 * trafega em JSON.
 *
 * Duas regras não negociáveis:
 *
 * 1. **Nenhuma ação é executada aqui.** O componente só emite `acao` com o
 *    `codigo`; quem mapeia código → handler é o registry da view. `endpoint` e
 *    `metodo` do payload existem para documentação e chamado — nunca para
 *    execução genérica.
 * 2. **A ação primária só aparece quando as duas condições valem, cumulativamente:**
 *    o backend diz `permitida: true` (regra de `normalizarBloqueio`) **e** a ação é o
 *    próximo passo disponível da sequência (regra de `avaliarAcaoNaSequencia`, aplicada
 *    dentro de `copyDoBloqueio`). A segunda nasceu do bloqueador da TMS-8133: o modal
 *    listava "passo 2 depende de um passo anterior" e oferecia o botão do passo 2, que
 *    leva `status_processo_expedicao` a 2 e queima o passo 1.
 *
 * 3. **A copy por família de guarda vive em `bloqueioCopy.js`** (TMS-8134), que é puro
 *    e testado. Este componente não decide texto: ele renderiza o que a copy devolve.
 *    Foi assim que o "motivo integral" e o "nunca 3 horas" viraram teste em vez de
 *    convenção.
 */
import { normalizarBloqueio } from "./sequenciaCancelamento";
import { copyDoBloqueio, explicacaoIndisponibilidade } from "./bloqueioCopy";

export default {
  name: "ExpedicaoBloqueioModal",
  data() {
    return {
      payload: null,
      rowData: null,
      passos: [],
      loading: false,
      detalhesAbertos: false,
    };
  },
  computed: {
    bloqueio() {
      return normalizarBloqueio(this.payload);
    },
    copy() {
      /* A sequência entra na copy: é ela que decide se a ação oferecida pelo
         backend é o próximo passo (TMS-8133). */
      return copyDoBloqueio(this.bloqueio, this.passosVisiveis);
    },
    titulo() {
      return this.copy.titulo;
    },
    detalhes() {
      return this.copy.detalhes;
    },
    acaoPrimaria() {
      return this.copy.acaoPrimaria;
    },
    acaoSecundaria() {
      return this.copy.acaoSecundaria;
    },
    acaoAlternativa() {
      return this.copy.acaoAlternativa;
    },
    passosVisiveis() {
      return Array.isArray(this.passos) ? this.passos : [];
    },
    codigoProximoPasso() {
      const proximo = this.passosVisiveis.find((passo) => passo.disponivel);
      return proximo ? proximo.codigo : null;
    },
  },
  methods: {
    /**
     * @param {object} payload envelope recusado pelo backend (com `bloqueio`)
     * @param {object} rowData linha da grid que originou a tentativa
     * @param {Array} passos saída de `passosPendentes`
     */
    abrir(payload, rowData, passos) {
      this.payload = payload;
      this.rowData = rowData || null;
      this.passos = Array.isArray(passos) ? passos : [];
      this.loading = false;
      this.detalhesAbertos = false;
      this.$refs.modal.show();
    },
    fechar() {
      this.$refs.modal.hide();
    },
    /* `ReturnApi.js:75` varre `vm.$refs` e chama `.hide()` em tudo que casa com
       "modal"; sem este método o modal ficaria preso aberto. */
    hide() {
      this.fechar();
    },
    setLoading(valor) {
      this.loading = Boolean(valor);
    },
    onHidden() {
      this.loading = false;
    },
    executar(codigo) {
      this.$emit("acao", {
        codigo,
        bloqueio: this.bloqueio,
        rowData: this.rowData,
      });
    },
    /* Delega para a copy — o texto é dela, e o mesmo é reusado no aviso de
       sequência. O wrapper existe só porque import não entra no escopo do
       template do Vue 2. */
    explicacaoIndisponibilidade(motivo) {
      return explicacaoIndisponibilidade(motivo);
    },
  },
};
</script>

<style scoped>
.bloqueio-modal-title {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
}

.bloqueio-modal-title-icon {
  margin-right: 8px;
  color: #e0a800;
}

.bloqueio-modal-message {
  margin-bottom: 12px;
  white-space: pre-line;
}

.bloqueio-modal-prazo {
  font-weight: 600;
  margin-bottom: 12px;
}

.bloqueio-modal-passos {
  background: #f7f7f9;
  border-radius: 4px;
  padding: 12px 12px 12px 8px;
  margin-bottom: 12px;
}

.bloqueio-modal-passos-titulo {
  margin-bottom: 6px;
  font-weight: 600;
}

.bloqueio-modal-passos-lista {
  margin-bottom: 0;
  padding-left: 24px;
}

.bloqueio-modal-passo-proximo {
  font-weight: 600;
}

.bloqueio-modal-passo-indisponivel {
  color: #6c757d;
}

.bloqueio-modal-sequencia {
  margin-bottom: 12px;
  font-weight: 600;
}

.bloqueio-modal-etapas {
  margin-bottom: 12px;
}

.bloqueio-modal-sem-acao {
  margin-bottom: 12px;
}

.bloqueio-modal-detalhes {
  margin-bottom: 12px;
}

.bloqueio-modal-detalhes-toggle {
  padding-left: 0;
}

.bloqueio-modal-detalhes-tabela th {
  width: 190px;
  font-weight: 600;
  white-space: nowrap;
}

.bloqueio-modal-detalhes-tabela td {
  word-break: break-all;
}

.bloqueio-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
</style>
